// Thin fetch wrapper for calling the Express backend (server/index.js) and,
// via the same Authorization: Bearer token, the FR-8 Quiz Service (Python,
// separately hosted - see services/dynamic-practice-engine/quiz_service).
// Both verify the same JWT server/index.js's /api/auth/refresh issues
// (docs/DECISIONS.md, 2026-07-28 "FR-8 Quiz Portal: auth architecture...").

const SERVER_ORIGIN = process.env.REACT_APP_SERVER_ORIGIN || 'http://localhost:4000';

// The FR-8 Quiz Service (Python/FastAPI, services/dynamic-practice-engine/
// quiz_service) is a separate process from the Express server above -
// default port matches uvicorn's own default (8000).
export const QUIZ_SERVICE_ORIGIN = process.env.REACT_APP_QUIZ_SERVICE_ORIGIN || 'http://localhost:8000';

// FR-14 Admin Service (services/dynamic-practice-engine/admin_service) - a
// separate FastAPI process from the Quiz Service, run on its own port so
// the two can scale/deploy independently per SRS Section 6.1's microservice
// view. Uses the same login JWT as apiFetch (RBAC is an ADMIN_EMAILS
// allowlist check server-side, not a separate token type).
export const ADMIN_SERVICE_ORIGIN = process.env.REACT_APP_ADMIN_SERVICE_ORIGIN || 'http://localhost:8001';

// Self-service student registration (docs/DECISIONS.md, "student-facing UI
// batch: curriculum rebrand + registration") - another separate FastAPI
// process locally, same as ADMIN_SERVICE_ORIGIN/QUIZ_SERVICE_ORIGIN above.
// In production all of these resolve to the one shared API Gateway origin
// already (services/dynamic-practice-engine/template.yaml's single HttpApi).
export const STUDENT_PROFILE_SERVICE_ORIGIN = process.env.REACT_APP_STUDENT_PROFILE_SERVICE_ORIGIN || 'http://localhost:8002';

// Self-practice syllabus/question service (docs/DECISIONS.md, "Self-practice:
// NCERT ingestion + server-side question bank") - another separate FastAPI
// process locally, same as the origins above; resolves to the same shared
// API Gateway origin in production.
export const PRACTICE_SERVICE_ORIGIN = process.env.REACT_APP_PRACTICE_SERVICE_ORIGIN || 'http://localhost:8003';

// Parent Login (docs/DECISIONS.md, 2026-08-25 "Parent login..." decision) -
// another separate FastAPI process locally, same as the origins above;
// resolves to the same shared API Gateway origin in production.
export const PARENT_SERVICE_ORIGIN = process.env.REACT_APP_PARENT_SERVICE_ORIGIN || 'http://localhost:8004';

// LEXA (docs/DECISIONS.md, 2026-08-28 "LEXA - Phase 1") - another separate
// FastAPI process locally (uvicorn lexa_service.main:app), same as the
// origins above; resolves to the same shared API Gateway origin in
// production.
export const LEXA_SERVICE_ORIGIN = process.env.REACT_APP_LEXA_SERVICE_ORIGIN || 'http://localhost:8005';

export function getStoredAuthToken() {
  return localStorage.getItem('authToken');
}

// Centralized session-expiry handling (docs/DECISIONS.md, "Bug-report
// batch: session/exam-flow/notification/curriculum fixes") - before this,
// only QuizPortal/ProtectedRoute redirected an expired session to /login;
// every other apiFetch caller (curriculum, registration, student profile,
// analytics, most of Admin) had no 401 handling at all, so an expired
// session either surfaced as a generic/blank error or (Admin) a
// permanent in-page "access denied" message. `SessionExpiryHandler.jsx`
// registers a handler here once, from inside the Router (where
// useNavigate/useAuth's logout are available - this module itself isn't a
// component and can't call hooks), so every apiFetch caller gets a
// consistent redirect to /login for free with no per-page code.
//
// Deliberately scoped to apiFetch (the general login-token path) only,
// not fetchWithToken: QuizPortal's exam-scoped session-token calls have
// their own deliberate 401 semantics already (a fallback-to-a-different-
// token retry on the results fetch, and its own explicit logout+redirect
// on /start) that a blanket redirect here would short-circuit
// incorrectly.
let sessionExpiredHandler = null;
export function registerSessionExpiredHandler(handler) {
  sessionExpiredHandler = handler;
}

/**
 * Exchange a Google ID token (credential) for a server-issued session JWT.
 * Mirrors server/index.js's POST /api/auth/refresh contract:
 * request { credential }, response { token, expiresAt, user }.
 */
export async function refreshSession(credential) {
  const response = await fetch(`${SERVER_ORIGIN}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });

  if (!response.ok) {
    throw new Error(`auth refresh failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Silently exchange a still-valid session JWT for a fresh one with a full
 * new TTL, without needing a new Google credential - the same
 * POST /api/auth/refresh endpoint already accepts a Bearer token as an
 * alternative to a credential in its body (server/index.js's
 * verifyServerSessionToken-then-fall-back-to-Google logic; ported as-is to
 * server/lambdas/auth-refresh/handler.js). Used by AuthContext's
 * inactivity-based session extension (docs/DECISIONS.md, "Login session:
 * true inactivity-based extension") - called periodically while the user is
 * active, so the session only actually expires after real idle time, not a
 * fixed time-since-login.
 */
export async function extendSession(token) {
  const response = await fetch(`${SERVER_ORIGIN}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`session extend failed: ${response.status}`);
  }

  return response.json();
}

/**
 * fetch() against `origin` (defaults to the Express server) with the
 * stored session JWT attached as `Authorization: Bearer <token>`, if one is
 * present. Callers targeting the Quiz Service pass its own origin
 * explicitly (e.g. process.env.REACT_APP_QUIZ_SERVICE_ORIGIN).
 */
export async function apiFetch(path, options = {}, origin = SERVER_ORIGIN) {
  const token = getStoredAuthToken();
  const response = await fetchWithToken(path, token, options, origin);
  if (response.status === 401 && sessionExpiredHandler) {
    sessionExpiredHandler();
  }
  return response;
}

/**
 * Like apiFetch, but attaches an explicitly-passed token instead of the
 * stored general-login session token. Used for the FR-8 Quiz Service's
 * exam-scoped session token (distinct from the login token - see
 * docs/DECISIONS.md, 2026-07-28 "FR-8 Quiz Portal: auth architecture...").
 */
export async function fetchWithToken(path, token, options = {}, origin = SERVER_ORIGIN) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  return fetch(`${origin}${path}`, { ...options, headers });
}
